



















































import {
  defineComponent,
  reactive,
  onMounted,
  onUnmounted,
  ref,
  computed,
  watch,
} from '@vue/composition-api';
import axios from 'axios';
import { useFormat } from '@ideavivat/vivat-2';

import Dropzone from '@/components/Dropzone/Dropzone.vue';

type DropzoneFile = {
  status: string;
  upload: { progress: number; bytesSent: number };
}

const CommonDropzoneWithAxios = defineComponent({
  components: {
    Dropzone,
  },
  props: {
    axios: {
      default: axios,
    },
    forName: {
      type: String,
      default: '',
    },
    endpoint: {
      required: true,
    },
    fileVariableName: {
      type: String,
      default: 'file',
    },
    variables: {
      type: Object,
      default: () => ({}),
    },
    maxFilesize: { // MB
      type: Number,
      default: 5,
    },
    acceptedFiles: { // csv, mime or .ext ie: image/*,application/pdf,.psd
      type: String,
      default: null,
    },
    storageLimitQuery: {
      default: null,
    },
    storageLimitVariables: {
      type: Object,
      default: () => ({}),
    },
    storageLimitResult: {
      type: Function,
      default: (data: any) => data,
    }, // Example: (data) => data.companyLimit.storageLeft
  },
  setup(props: any, context) {
    let fileQueue = reactive([] as (File & DropzoneFile)[]);
    const isSupportProgress = ref(false);
    let isQueueInProgress = false;

    const fileStatus = reactive({
      status: '',
      name: '',
      bytes: '',
      progressPercentage: '',
      progressBytes: '',
    });

    const fileBuffer = ref({} as File & DropzoneFile);

    function handleUploadProgress(f: File & DropzoneFile) {
      fileBuffer.value = f;
      fileStatus.status = fileBuffer.value.status;
      fileStatus.name = fileBuffer.value.name;
      fileStatus.bytes = useFormat.bytes(fileBuffer.value.size);
      fileStatus.progressPercentage = fileBuffer.value.upload.progress.toFixed(2);
      fileStatus.progressBytes = useFormat.bytes(fileBuffer.value.upload.bytesSent);
    }

    // To check for limit

    const isStorageLimitReached = ref(false);
    const storageLimitCache = ref(-1);
    const storageLimit = computed(() => useFormat.bytes(storageLimitCache.value));

    // const { result: resultLimit, refetch: refetchLimit } = useQuery(
    //   props.storageLimitQuery as any,
    //   computed(() => ({
    //     ...(props as any).storageLimitVariables,
    //   })),
    //   {
    //     fetchPolicy: 'network-only',
    //     enabled: props.storageLimitQuery !== null,
    //   },
    // );

    // const storageLimitResultNetwork = useResult(resultLimit, -1, (returnVal: any) => props.storageLimitResult(returnVal));
    // watch(storageLimitResultNetwork, () => {
    //   console.log(`[CommonDropzone] Network limit storage at ${storageLimitResultNetwork.value} (|| -1)`);
    //   storageLimitCache.value = Number(storageLimitResultNetwork.value || -1);
    // });

    async function processQueue() {
      isStorageLimitReached.value = false; // Clear flag

      if (fileQueue.length === 0) return;
      if (isQueueInProgress) return;

      handleUploadProgress(fileQueue[0]);

      // If limit is reached
      const filesizeBytes = fileBuffer.value.size;
      if (props.storageLimitQuery && storageLimitCache.value !== -1 && filesizeBytes > storageLimitCache.value) {
        isStorageLimitReached.value = true;
        fileQueue = []; // Remove all pending files
        context.emit('notEnoughStorage');
      }

      if (!isStorageLimitReached.value) {
        isQueueInProgress = true;
        const formData = new FormData();
        formData.append(props.fileVariableName, fileQueue[0]);
        // eslint-disable-next-line
        for (const [key, value] of Object.entries(props.variables)) {
          formData.append(key, value as any);
        }

        const axiosResult = await props.axios.post(props.endpoint, formData, {
          ...props.axios.defaults.headers.common,
          'Content-Type': 'multipart/form-data',
          onUploadProgress: (ev: ProgressEvent) => {
            if (ev.loaded) {
              isSupportProgress.value = true;
              fileStatus.progressBytes = useFormat.bytes(ev.loaded);
              fileStatus.progressPercentage = ((ev.loaded / ev.total) * 100).toFixed(2);
            }
          },
        }).then((response: any) => {
          if (response.status === 200 || response.status === 200) {
            return response.data;
          }
          return 'not 200';
        }).catch((error: any) => {
          if (error.response) {
            // Request made and server responded
            console.log(error.response.data);
            console.log(error.response.status);
            console.log(error.response.headers);
          } else if (error.request) {
            // The request was made but no response was received
            console.log(error.request);
          } else {
            // Something happened in setting up the request that triggered an Error
            console.log('Error', error.message);
          }
          return 'THERE IS AN ERROR';
        });

        isQueueInProgress = false;
        isSupportProgress.value = false;
        fileStatus.progressPercentage = '100.00';
        context.emit('done', axiosResult);
        fileQueue.shift();

        // if limit is applied
        if (props.storageLimitQuery && storageLimitCache.value !== -1) {
          storageLimitCache.value -= filesizeBytes; // Remove from cache
          // refetchLimit(); // make it happens async
        }

        if (fileQueue.length > 0) {
          fileBuffer.value = {} as File & DropzoneFile;
          setTimeout(() => { processQueue(); }, 1);
        } else {
          isQueueInProgress = false;
          setTimeout(() => {
            fileBuffer.value = {} as File & DropzoneFile;
          }, 5000);
        }
      }
    }

    async function addAndProcessQueue(f: File & DropzoneFile) {
      fileQueue.push(f);
      processQueue();
    }

    const dropzoneOptions = computed(() => ({
      url: '/',
      maxFilesize: props.maxFilesize,
      createImageThumbnails: false,
      autoProcessQueue: false,
      acceptedFiles: props.acceptedFiles,
      accept(f: File & DropzoneFile, done: () => {}) {
        addAndProcessQueue(f);
        done();
      },
    }));

    const isDragging = ref(false);
    const isHovering = ref(false);

    function isDraggingFile(e: DragEvent) {
      if (e && e.dataTransfer) {
        const dt = e.dataTransfer;
        if (dt.types && dt.types.indexOf('Files') !== -1) {
          return true;
        }
      }
      return false;
    }

    function handleDragInDocument(e: DragEvent) {
      if (isDraggingFile(e)) isDragging.value = true;
    }

    function handleDragOutDocument(e: DragEvent) {
      if (isDraggingFile(e) && e.x === 0 && e.y === 0) isDragging.value = false;
    }

    function handleFileAdd(f: File & DropzoneFile) {
      handleUploadProgress(f);
      console.log(f);
      console.log('file add');
    }

    function handleFileDrop() {
      isDragging.value = false;
      isHovering.value = false;
    }

    function handleUploadError(f: File & DropzoneFile, error: string) {
      console.log(error);
      fileStatus.status = 'error';
      setTimeout(() => {
        fileBuffer.value = {} as File & DropzoneFile;
      }, 5000);
    }

    // TODO: emit events
    const isFileActive = computed(() => 'status' in fileBuffer.value);
    const isUploadFinished = computed(() => parseInt(fileStatus.progressPercentage, 10) >= 100);

    // (props.mutation as any).onDone(() => {
    //   fileStatus.progressPercentage = '100.00';
    //   isSupportProgress.value = false;
    //   context.emit('done', fileQueue[0]);
    //   fileQueue.shift();
    //   if (fileQueue.length > 0) {
    //     processQueue();
    //   } else {
    //     setTimeout(() => {
    //       fileBuffer.value = {} as File & Vue2DropzoneFile;
    //     }, 5000);
    //   }
    // });

    onMounted(() => {
      window.addEventListener('dragover', handleDragInDocument);
      window.addEventListener('dragleave', handleDragOutDocument);
    });

    onUnmounted(() => {
      window.removeEventListener('dragover', handleDragInDocument);
      window.removeEventListener('dragleave', handleDragOutDocument);
    });

    return {
      fileQueue,
      isSupportProgress,
      handleUploadError,
      dropzoneOptions,
      isDragging: true, // TODO: fix fixed position bug, some others relative element still appear
      isHovering,
      isStorageLimitReached,
      storageLimit,
      handleUploadProgress,
      handleFileDrop,
      isFileActive,
      isUploadFinished,
      fileStatus,
      handleFileAdd,
    };
  },
});

export default CommonDropzoneWithAxios;

